import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";
import { Card } from "clutch/src/Card/Card.js";

import { readState } from "@/__main__/app-state.mjs";
import { IS_PROBUILDS } from "@/__main__/constants.mjs";
import { GAME_SHORT_NAMES } from "@/app/constants.mjs";
import { Section } from "@/marketing/ContentfulArticles.style.jsx";
import type { ContentfulFeatures } from "@/marketing/HomeGamePage.js";
import BlitzLogoLoading from "@/shared/BlitzLogoLoading.jsx";
import { CONTENTFUL_TAGS } from "@/shared/contentful-constants.mjs";
import { useGameSymbol } from "@/util/game-route.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const SLOTS = 7;

export default function ContentfulArticleGrid({
  title,
  description,
  cta,
  linkConstructor,
}: ContentfulFeatures) {
  const gameSymbol = useGameSymbol();
  const { t } = useTranslation();
  const {
    contentful: { related },
  } = useSnapshot(readState);

  const shortName = useMemo(() => GAME_SHORT_NAMES[gameSymbol], [gameSymbol]);
  const items = useMemo(
    () =>
      related
        .filter((article) =>
          [
            CONTENTFUL_TAGS[shortName],
            IS_PROBUILDS && CONTENTFUL_TAGS.PROBUILDS,
            CONTENTFUL_TAGS.TILES_GRID,
          ]
            .filter(Boolean)
            .every((i) => article.tags.some((j) => j === i)),
        )
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, SLOTS),
    [related, shortName],
  );

  const hasTitle = Array.isArray(title);
  const hasDescription = Array.isArray(description);
  const hasCta = cta && Array.isArray(cta);

  return (
    <Section
      style={{ containerType: "inline-size", containerName: "article-grid" }}
    >
      {(hasTitle || !!description) && (
        <header>
          <div className="header-left">
            {hasTitle && (
              <h2 className="section-title type-page-header">{t(...title)}</h2>
            )}
            {hasDescription && (
              <p className="section-subtitle type-subtitle--bold">
                {t(...description)}
              </p>
            )}
          </div>
          {hasCta ? (
            <a href={cta.href} className="view-more type-subtitle--semi">
              {t(...cta.title)}
            </a>
          ) : null}
        </header>
      )}
      <div className={cssGrid()}>
        {items.length
          ? new Array(SLOTS).fill(undefined).map((_, i) => {
              const tile = items[i];
              if (!tile) {
                return <div key={i} className="placeholder" />;
              }
              return (
                <a href={tile.redirect ?? linkConstructor(tile)} key={tile.id}>
                  <img
                    src={tile.imageUrl}
                    alt={`${tile.title} blog article`}
                    loading="lazy"
                    className="grid-image"
                    width={i === 0 ? 280 : 140}
                    height={i === 0 ? 280 : 140}
                  />
                  <article>
                    <h3 className="article-title">{tile.title}</h3>
                    <p className="article-description" data-show={i === 0}>
                      {tile.excerpt}
                    </p>
                  </article>
                </a>
              );
            })
          : new Array(7).fill(undefined).map((_, idx) => (
              <div className="relative" key={idx} style={{ aspectRatio: 1 }}>
                <Card
                  loading
                  classNameOuter="w-full h-full"
                  className="w-full h-full"
                />
                <div className="absolute-center">
                  <BlitzLogoLoading />
                </div>
              </div>
            ))}
      </div>
    </Section>
  );
}

const cssGrid = () =>
  css({
    a: {
      "--radii": "var(--br-xl)",
      position: "relative",
      borderRadius: "var(--radii)",
      overflow: "clip",
      aspectRatio: 1,

      "&:hover": {
        ".grid-image": {
          scale: 1.1,
        },
      },

      "&::after": {
        content: "''",
        position: "absolute",
        inset: 0,
        border: "1px solid white",
        borderRadius: "var(--radii)",
        pointerEvents: "none",
        opacity: 0.15,
        zIndex: 1,
      },
    },
    ".placeholder": {
      aspectRatio: 1,
    },
    article: {
      position: "absolute",
      bottom: 0,
      left: 0,
      width: "100%",
      padding: "var(--sp-4)",
      borderRadius: "var(--radii)",
      textShadow: "0 3px 23px black",
    },
    ".article-title": {
      lineHeight: "1.25",
    },
    ".article-description": {
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: "2",
      display: "-webkit-box",
      fontSize: "0.75em",
      opacity: 0.75,
    },
    ".grid-image": {
      position: "absolute",
      top: "50%",
      left: "50%",
      translate: "-50% -50%",
      objectFit: "cover",
      aspectRatio: 1,
      width: "100%",
      height: "auto",
      borderRadius: "var(--radii)",
      maskImage: "linear-gradient(to bottom, black, hsl(0deg 0% 0% / 10%) 75%)",
      transition: "scale var(--transition)",
    },
    ".article-description[data-show='false']": {
      display: "none",
    },
    "& > *:nth-child(1)": {
      gridColumn: "1 / 3",
      gridRow: "1 / 3",
      fontSize: "1.5rem",

      article: {
        padding: "var(--sp-6)",
      },
    },
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gridTemplateRows: "repeat(2, 1fr)",
    gap: "var(--sp-4)",
    "@container article-grid (inline-size <= 800px)": {
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "unset",

      ".placeholder": {
        display: "none",
      },

      "& > *:nth-child(1)": {
        fontSize: "unset",
        gridColumn: "unset",
        gridRow: "unset",

        article: {
          padding: "var(--sp-4)",
        },
      },

      ".article-description": {
        display: "none",
      },
    },
  });
